import {
  Alarm,
  AuditLog,
  Backup,
  Auth,
  Config,
  Contact,
  Device,
  EventMapping,
  FeatureFlags,
  Location,
  Notification,
  Room,
  Scanner,
  User,
  RestApiError,
  isFeatureEnabled,
  type FeatureFlag,
} from '@evac/shared'
import type { NavigationGuardWithThis, RouteRecordRaw } from 'vue-router'
import { type Endpoints, getEndpoints } from '@/rest'

const loadedEntities = new Set<string>()

export function resetLoadedEntities() {
  loadedEntities.clear()
}

function loadData(...entityKeys: (keyof Endpoints)[]): NavigationGuardWithThis<undefined> {
  return async () => {
    const endpoints = getEndpoints()
    const fetches = entityKeys
      .filter((key) => !loadedEntities.has(key))
      .map(async (key) => {
        await endpoints[key].fetchAll()
        loadedEntities.add(key)
      })
    try {
      await Promise.all(fetches)
    } catch (error) {
      if (error instanceof RestApiError && error.isUnauthorized) {
        // onUnauthorized already called logout(), redirect to dashboard (login wall)
        return { name: 'dashboard' }
      }
      throw error
    }
  }
}

export default [
  {
    path: 'locations',
    name: 'config.locations',
    component: Location.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.locations), loadData('location', 'room')],
  },
  {
    path: 'rooms',
    name: 'config.rooms',
    component: Room.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.rooms), loadData('room', 'location', 'scanner')],
  },
  {
    path: 'scanners',
    name: 'config.scanners',
    component: Scanner.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.scanners), loadData('scanner', 'room')],
  },
  {
    path: 'devices',
    name: 'config.devices',
    component: Device.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.devices), loadData('device', 'activity', 'scanner', 'room', 'location')],
  },
  {
    path: 'alarms',
    name: 'config.alarms',
    component: Alarm.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.alarms), loadData('alarm', 'notification', 'contact', 'contactGroup')],
  },
  {
    path: 'notifications',
    name: 'config.notifications',
    component: Notification.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.notifications), loadData('notification')],
  },
  {
    path: 'contacts',
    name: 'config.contacts',
    component: Contact.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.contacts), loadData('contact', 'contactGroup')],
  },
  {
    path: 'event-mappings',
    name: 'config.eventMappings',
    component: EventMapping.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.eventMappings), loadData('eventMapping', 'notification', 'contactGroup')],
  },
  {
    path: 'users',
    name: 'config.users',
    component: User.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.users), loadData('user')],
  },
  {
    path: 'backups',
    name: 'config.backups',
    component: Backup.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.backups), loadData('backup')],
  },
  {
    path: 'audit-log',
    name: 'config.auditLog',
    component: AuditLog.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.auditLog), guardFeatureFlag('audit')],
  },
  // The Backups page used to be Tools; keep old bookmarks working.
  { path: 'tools', redirect: { name: 'config.backups' } },
  {
    path: 'config',
    name: 'config.config',
    component: Config.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.config), loadData('config')],
  },
  {
    path: 'feature-flags',
    name: 'config.featureFlags',
    component: FeatureFlags.views.Config,
    beforeEnter: [guardAccessRights(Config.NAVIGATION_VISIBILITY.featureFlags), guardFeatureFlag('page')],
  },
] satisfies RouteRecordRaw[]

function guardAccessRights(roles: Set<Auth.Role>) {
  return async () => {
    const isAllowed = await Auth.validateUserRoles(roles)
    if (isAllowed) return true

    const fallbackRouteName = (await Config.findAllowedRoute()) ?? 'dashboard'
    return { name: fallbackRouteName }
  }
}

function guardFeatureFlag(flag: FeatureFlag) {
  return async () => {
    if (isFeatureEnabled(flag)) return true

    const fallbackRouteName = (await Config.findAllowedRoute()) ?? 'dashboard'
    return { name: fallbackRouteName }
  }
}
